import { c as _c } from "react/compiler-runtime";
import { Clock4, MapPin, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { daysUntil, formatPct, formatUsd } from "@/lib/format";
import type { Offering } from "@/types/residual";
const statusLabel: Record<Offering["status"], string> = {
  draft: "Draft",
  tokenized: "Tokenized",
  sale: "Open for sale",
  funded: "Funded",
  active: "Generating income",
  failed: "Refunded"
};
const statusTone: Record<Offering["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  tokenized: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  sale: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  funded: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  active: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  failed: "bg-rose-500/15 text-rose-600 dark:text-rose-400"
};
export function OfferingCard(t0) {
  const $ = _c(56);
  const {
    offering,
    onClick
  } = t0;
  const o = offering;
  const fundedPct = o.soldTokens / o.tokenSupply * 100;
  const days = daysUntil(o.deadlineAt);
  const t1 = `url(${o.heroUrl})`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className="aspect-[16/9] w-full bg-muted bg-cover bg-center" style={{
      backgroundImage: t1
    }} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== o.title) {
    t3 = <h3 className="text-base font-semibold leading-tight">{o.title}</h3>;
    $[2] = o.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== o.subtitle) {
    t4 = <p className="text-sm text-muted-foreground line-clamp-1">{o.subtitle}</p>;
    $[4] = o.subtitle;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div>{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusTone[o.status]}`;
  const t7 = statusLabel[o.status];
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t5 || $[13] !== t8) {
    t9 = <div className="flex items-start justify-between gap-2">{t5}{t8}</div>;
    $[12] = t5;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = o.kind === "asset" ? "Asset ownership" : "Revenue share";
  let t11;
  if ($[15] !== t10) {
    t11 = <Badge variant="secondary" className="rounded-full font-normal">{t10}</Badge>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== o.location) {
    t12 = o.location && <Badge variant="outline" className="gap-1 rounded-full font-normal"><MapPin className="size-3" />{o.location}</Badge>;
    $[17] = o.location;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== o.projectedYearlyYieldPct) {
    t13 = o.projectedYearlyYieldPct !== undefined && <Badge variant="outline" className="gap-1 rounded-full font-normal text-emerald-700 dark:text-emerald-400"><TrendingUp className="size-3" />{formatPct(o.projectedYearlyYieldPct)} target</Badge>;
    $[19] = o.projectedYearlyYieldPct;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t12 || $[23] !== t13) {
    t14 = <div className="flex flex-wrap items-center gap-1.5">{t11}{t12}{t13}</div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== o.tokenPrice) {
    t15 = formatUsd(o.tokenPrice);
    $[25] = o.tokenPrice;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== o.tokenSymbol) {
    t16 = <span className="text-xs text-muted-foreground">/ {o.tokenSymbol}</span>;
    $[27] = o.tokenSymbol;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <span className="font-medium">{t15} {t16}</span>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== fundedPct) {
    t18 = Math.round(fundedPct);
    $[32] = fundedPct;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = <span className="text-xs text-muted-foreground">{t18}% funded</span>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t17 || $[37] !== t19) {
    t20 = <div className="mb-1 flex items-baseline justify-between text-sm">{t17}{t19}</div>;
    $[36] = t17;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== fundedPct) {
    t21 = <Progress value={fundedPct} className="h-1.5" />;
    $[39] = fundedPct;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <div>{t20}{t21}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== days || $[45] !== o.status) {
    t23 = o.status === "sale" && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock4 className="size-3.5" />{days > 0 ? `Closes in ${days} day${days === 1 ? "" : "s"}` : "Closing today"}</div>;
    $[44] = days;
    $[45] = o.status;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t14 || $[48] !== t22 || $[49] !== t23 || $[50] !== t9) {
    t24 = <div className="space-y-3 p-4">{t9}{t14}{t22}{t23}</div>;
    $[47] = t14;
    $[48] = t22;
    $[49] = t23;
    $[50] = t9;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== onClick || $[53] !== t2 || $[54] !== t24) {
    t25 = <button type="button" onClick={onClick} className="group block w-full overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition-shadow hover:shadow-md">{t2}{t24}</button>;
    $[52] = onClick;
    $[53] = t2;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  return t25;
}